import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Full Name", placeholder: "Roshan Ali", type: "text", required: true },
  { name: "business_name", label: "Business Name", placeholder: "Acme Signage LLC", type: "text", required: true },
  { name: "designation", label: "Designation", placeholder: "Founder / Owner", type: "text", required: true },
  { name: "email", label: "Email", placeholder: "you@business.com", type: "email", required: true },
  { name: "phone", label: "Phone Number", placeholder: "+971 50 000 0000", type: "tel", required: true },
  { name: "website", label: "Website (optional)", placeholder: "https://yourbusiness.com", type: "text", required: false },
];

const EMPTY = { name: "", business_name: "", designation: "", email: "", phone: "", website: "" };

export const EarlyAccessForm = ({ source = "early_access" }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, { ...form, source });
      setDone(true);
      setForm(EMPTY);
      toast.success(source === "contact" ? "Message sent!" : "You're on the list!", {
        description:
          source === "contact"
            ? "Thanks for reaching out — our team will get back to you shortly."
            : "Our team will reach out about your Workpay early access.",
      });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please check your details and try again." });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="early-access-success"
        className="bg-white rounded-3xl p-10 text-center flex flex-col items-center gap-4 soft-shadow border border-slate-100"
      >
        <div className="h-16 w-16 rounded-full bg-[#ecfdf3] flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-[#16a34a]" />
        </div>
        <h3 className="text-2xl font-display font-bold text-[#091630]">
          {source === "contact" ? "Message received" : "Request received"}
        </h3>
        <p className="text-slate-500 max-w-sm">
          {source === "contact"
            ? "Thanks for contacting Workpay. Our team will get back to you as soon as possible."
            : "Thanks for joining the Workpay early access list. We'll be in touch shortly to get you set up."}
        </p>
        <Button
          data-testid="early-access-reset-button"
          variant="ghost"
          onClick={() => setDone(false)}
          className="text-[#16a34a] hover:text-[#16a34a] hover:bg-[#ecfdf3]"
        >
          {source === "contact" ? "Send another message" : "Submit another request"}
        </Button>
      </div>
    );
  }

  return (
    <form
      data-testid="early-access-form"
      onSubmit={handleSubmit}
      className="bg-white rounded-3xl p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5 soft-shadow border border-slate-100"
    >
      {FIELDS.map((field) => (
        <div key={field.name} className={`flex flex-col gap-1.5 ${field.name === "website" ? "sm:col-span-2" : ""}`}>
          <Label htmlFor={field.name} className="text-sm text-slate-600 font-semibold">
            {field.label}
          </Label>
          <Input
            id={field.name}
            name={field.name}
            type={field.type}
            required={field.required}
            value={form[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            data-testid={`field-${field.name}`}
            className="bg-slate-50 border-slate-200 text-[#091630] placeholder:text-slate-400 h-12 rounded-xl focus-visible:ring-[#21c55e] focus-visible:border-[#21c55e]"
          />
        </div>
      ))}

      <div className="sm:col-span-2 mt-1">
        <Button
          type="submit"
          disabled={loading}
          data-testid="early-access-submit-button"
          className="w-full py-4 h-auto bg-[#21c55e] text-[#091630] font-bold text-base rounded-full hover:bg-[#16a34a] hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> Submitting…
            </>
          ) : (
            <>
              {source === "demo" ? "Book a Demo" : source === "contact" ? "Send message" : "Request Early Access"}{" "}
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </Button>
        {source !== "contact" && (
          <p className="text-xs text-slate-400 text-center mt-3">
            Pricing may change after public launch. Additional processing charges may apply for international
            cards, refunds, chargebacks, or currency conversion.
          </p>
        )}
        <p data-testid="form-consent" className="text-xs text-slate-400 text-center mt-3">
          By submitting this form, you agree that WorkPay may contact you about your enquiry. Please read our{" "}
          <Link to="/privacy-policy" data-testid="form-consent-privacy" className="font-semibold text-[#16a34a] hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </form>
  );
};

export default EarlyAccessForm;
